import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { MAX_CACHE_MS, getCreative, getRecord, getStates, searchRecords } from '../api/client'
import { isRetryable } from '../api/errors'
import type { SearchParams } from '../types'

/** Shared retry policy — see isRetryable: never 429, never an ordinary 4xx. */
const retry = (failureCount: number, error: unknown) => isRetryable(error) && failureCount < 2

/**
 * The state list drives the search form.
 *
 * Held for the API's own max-age and no longer: published records are corrected in
 * place with no version history, so an over-long client cache can present a
 * superseded legal record as current.
 */
export function useStatesQuery() {
  return useQuery({
    queryKey: ['states'],
    queryFn: ({ signal }) => getStates(signal),
    staleTime: MAX_CACHE_MS,
    retry,
  })
}

/**
 * Search results. Disabled until at least one state is selected, because the API
 * requires `states` and would return 400.
 */
export function useSearchQuery(params: SearchParams, enabled: boolean) {
  return useQuery({
    queryKey: ['search', params],
    queryFn: ({ signal }) => searchRecords(params, signal),
    enabled: enabled && params.states.length > 0,
    // Keeps the previous page on screen while the next one loads, instead of
    // flashing an empty table.
    placeholderData: keepPreviousData,
    staleTime: MAX_CACHE_MS,
    retry,
  })
}

/**
 * Record detail. `states` narrows the figures to the states the user searched;
 * the caller is responsible for passing only codes that came from /states.
 */
export function useRecordQuery(id: string | undefined, states: string[], enabled = true) {
  return useQuery({
    queryKey: ['record', id, states],
    queryFn: ({ signal }) => getRecord(id as string, states, signal),
    enabled: Boolean(id) && enabled,
    staleTime: MAX_CACHE_MS,
    retry,
  })
}

/**
 * Creative fetch. Deliberately on-demand and uncached:
 *  - `enabled` is passed by the detail page, so nothing is prefetched from a list
 *  - `staleTime: 0` / `gcTime: 0` keep the signed URL out of the cache; it expires
 *    in ~15 minutes and must not be stored or reused
 *  - `retry: false` because a 404 here is a normal outcome, not a fault
 */
export function useCreativeQuery(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['creative', id],
    queryFn: ({ signal }) => getCreative(id as string, signal),
    enabled: Boolean(id) && enabled,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    retry: false,
    // Never escalated to the error boundary: the record itself loaded, and a
    // missing advertisement must not blank a published legal record.
    throwOnError: false,
  })
}
